import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ChevronRight, Megaphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const SponsorStripBanner = ({ sportType, region }) => {
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const params = new URLSearchParams();
        if (sportType) params.append('sport_type', sportType);
        if (region) params.append('region', region);
        const res = await api.get(`/sponsors/banners?format=strip&${params.toString()}`);
        const sponsorBanners = res.data.banners || [];

        const all = [];
        for (const b of sponsorBanners) {
          all.push(b);
        }
        // Always add internal promo at the end
        all.push({
          id: 'internal_strip',
          brand_name: 'SPORT ADS NETWORK',
          brand_color: '#ff4500',
          banner_text: t('sponsor.internalMsg2'),
          banner_cta: t('sponsor.internalCta2'),
          link_url: '/sponsoring',
          is_internal: true,
        });

        setBanners(all);
      } catch {
        setBanners([{
          id: 'internal_strip',
          brand_name: 'SPORT ADS NETWORK',
          brand_color: '#ff4500',
          banner_text: t('sponsor.internalMsg2'),
          banner_cta: t('sponsor.internalCta2'),
          link_url: '/sponsoring',
          is_internal: true,
        }]);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, [sportType, region, t]);

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setCurrent(prev => (prev + 1) % banners.length);
        setTransitioning(false);
      }, 300);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  const handleClick = useCallback(async (banner) => {
    if (banner.campaign_id) {
      try {
        await api.post('/sponsors/click', { campaign_id: banner.campaign_id, region });
      } catch {}
    }
  }, [region]);

  if (loading || banners.length === 0) return null;

  const banner = banners[current];
  const isInternal = banner.is_internal;

  const content = (
    <div
      className={`relative w-full overflow-hidden cursor-pointer group transition-opacity duration-300 ${transitioning ? 'opacity-0' : 'opacity-100'}`}
      style={{
        background: isInternal
          ? 'linear-gradient(90deg, #0f172a 0%, #1e293b 30%, #0f172a 70%, #1e293b 100%)'
          : `linear-gradient(90deg, #f8fafc 0%, ${banner.brand_color}12 50%, #f8fafc 100%)`,
        borderBottom: '1px solid #e2e8f0',
      }}
      onClick={() => handleClick(banner)}
      data-testid="sponsor-strip-banner"
    >
      {/* Accent line top */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: banner.brand_color || '#ff4500' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3 gap-4">
          {/* Left: brand + text */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {/* Brand badge */}
            <div className="flex-shrink-0 flex items-center gap-2">
              {isInternal ? (
                <Megaphone className="w-4 h-4 text-brand" />
              ) : banner.logo_url ? (
                <img
                  src={banner.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${banner.logo_url}` : banner.logo_url}
                  alt={banner.brand_name}
                  className="h-5 w-auto max-w-[60px] object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : null}
              <span
                className="font-heading font-bold uppercase tracking-[0.15em] text-[10px] whitespace-nowrap"
                style={{ color: banner.brand_color || '#ff4500' }}
              >
                {banner.brand_name}
              </span>
              {!isInternal && (
                <span className="text-[8px] text-slate-400 font-heading uppercase tracking-widest border border-slate-300 px-1.5 py-0.5">
                  {t('sponsor.ad')}
                </span>
              )}
            </div>

            {/* Separator */}
            <div className="hidden sm:block w-px h-6 bg-slate-300/30" />

            {/* Banner text */}
            <p className={`text-sm truncate ${isInternal ? 'text-slate-300' : 'text-slate-600'}`}>
              {banner.banner_text}
            </p>
          </div>

          {/* Right: CTA + dots */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <span
              className="hidden sm:inline-flex items-center gap-1 text-xs font-heading font-bold uppercase tracking-wider whitespace-nowrap transition-all group-hover:gap-2"
              style={{ color: banner.brand_color || '#ff4500' }}
            >
              {isInternal ? banner.banner_cta : banner.banner_cta}
              {isInternal ? <ChevronRight className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
            </span>

            {/* Dot nav */}
            {banners.length > 1 && (
              <div className="flex items-center gap-1">
                {banners.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrent(idx);
                    }}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      idx === current ? 'w-4' : ''
                    }`}
                    style={{
                      background: idx === current ? (banner.brand_color || '#ff4500') : (isInternal ? '#475569' : '#cbd5e1'),
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isInternal) {
    return <Link to={banner.link_url} data-testid="sponsor-strip-link">{content}</Link>;
  }

  return (
    <a href={banner.link_url} target="_blank" rel="noopener noreferrer" data-testid="sponsor-strip-link">
      {content}
    </a>
  );
};

export default SponsorStripBanner;
